import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import {
  TagState,
  VOneChipsTagsComponent,
  VOneIconComponent,
  VOneMenuDropdownComponent,
} from '@shared-components';
import { CardCode, CardState, DropdownItem } from '@shared-models';
import { isPortfolioCardState } from '@shared-utils';

interface ChipConfig {
  label: string;
  tagState: TagState;
  title?: string;
  indicatorColor?: string;
}

interface NoteChip {
  label: string;
  tagState: TagState;
}

@Component({
  selector: 'v-one-card-header',
  imports: [
    forwardRef(() => VOneIconComponent),
    forwardRef(() => VOneMenuDropdownComponent),
    forwardRef(() => VOneChipsTagsComponent),
    TranslatePipe,
  ],
  templateUrl: './card-header.component.html',
  styleUrl: './card-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardHeaderComponent {
  @Input() title = '';
  @Input() consecutives: CardCode[] = [];
  @Input() cardState: CardState = 'order';
  @Input() icon?: string;
  @Input() iconColor = '';
  @Input() indicatorColor = '';
  @Input() showMenu = true;
  @Input() menuOptions: DropdownItem[] = [];
  @Input() hasCreditNote = false;
  @Input() hasDebitNote = false;

  @Output() menuAction = new EventEmitter<string>();

  private static readonly NOTE_CHIPS: Record<'debit', NoteChip> = {
    debit: { label: 'card.notes.debit', tagState: 'warning' },
  };

  protected handleMenuSelect(value: string): void {
    this.menuAction.emit(value);
  }

  protected isPortfolioCard(): boolean {
    return isPortfolioCardState(this.cardState);
  }

  private static readonly CUFE_MIN_LENGTH = 50;

  private static isCufeCode(code: CardCode): boolean {
    return (code.value?.length ?? 0) >= CardHeaderComponent.CUFE_MIN_LENGTH;
  }

  protected getDisplayConsecutives(): CardCode[] {
    if (!this.isPortfolioCard() || this.consecutives.length < 2) {
      return this.consecutives;
    }

    const withoutCufe = this.consecutives.filter(
      (code) => !CardHeaderComponent.isCufeCode(code),
    );

    if (withoutCufe.length < 2) {
      return withoutCufe;
    }

    const reordered = [...withoutCufe];
    const lastIndex = reordered.length - 1;
    [reordered[1], reordered[lastIndex]] = [reordered[lastIndex], reordered[1]];

    return reordered;
  }

  protected getNoteChips(): NoteChip[] {
    // Cartera never shows the "Nota Débito" chip — payment status already
    // reflects notes there (see getChipConfig). Sales keeps it unchanged.
    if (this.isPortfolioCard()) return [];

    const chips: NoteChip[] = [];
    if (this.hasDebitNote) chips.push(CardHeaderComponent.NOTE_CHIPS.debit);
    return chips;
  }

  private static readonly CHIP_STATE_MAP: Partial<
    Record<CardState, ChipConfig>
  > = {
    'invoice-unpaid': {
      label: 'card.paymentStatus.unpaid',
      tagState: 'rejected',
      title: 'card.labels.pending',
      indicatorColor: 'var(--color-danger-700)',
    },
    'invoice-partial': {
      label: 'card.paymentStatus.partial',
      tagState: 'warning',
      title: 'card.labels.pending',
      indicatorColor: 'var(--color-warning-600)',
    },
    'invoice-fully-paid': {
      label: 'card.paymentStatus.paid',
      tagState: 'approved',
      indicatorColor: 'var(--color-success-600)',
    },
    'invoice-voided': {
      label: 'card.paymentStatus.voided',
      tagState: 'default',
      indicatorColor: 'var(--color-gray-500)',
    },
  };

  protected getChipConfig(): ChipConfig | null {
    return CardHeaderComponent.CHIP_STATE_MAP[this.cardState] ?? null;
  }

  protected isHighlighted(code: CardCode): boolean {
    const stateMap: Record<CardState, string> = {
      order: 'Order',
      delivery: 'Delivery',
      'invoice-without-notes': 'Invoice',
      'invoice-with-notes': 'Invoice',
      rejected: 'Order',
      'invoice-unpaid': 'Invoice',
      'invoice-partial': 'Invoice',
      'invoice-fully-paid': 'Invoice',
      'invoice-voided': 'Invoice',
    };

    return code.label.toLowerCase() === stateMap[this.cardState].toLowerCase();
  }
}
